import { Box } from "@mui/material";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { AUTH_CARD_STYLES } from "./authCardStyles";

export interface AuthCardProps {
  /** 단계가 있는 화면의 지금 단계 — 「2 / 3」 */
  step?: { current: number; total: number };
  title: string;
  /** 제목 아래 한두 줄 안내 */
  description?: ReactNode;
  /** 제목 아래 상태 안내(예: 다른 기기 로그인으로 밀려난 사유) */
  notice?: ReactNode;
  /** 카드가 나타나면 첫 칸에 초점(단계를 넘긴 뒤 바로 입력) — 마우스 기기만, 휴대폰은 자판이 갑자기 올라오지 않게 */
  focusFirstField?: boolean;
  children: ReactNode;
}

const POINTER_DEVICE_QUERY = "(hover: hover)";

/**
 * 로그인 계열 화면의 입력 카드(marketing-auth-pages): 지금 단계 · `h1` 제목 · 안내 · 입력 칸 · 주 버튼 · 보조 링크 줄을 이 순서로.
 * 단계가 바뀌면 새 카드가 아래에서 살짝 떠오른다. 가로로 둔 터치 화면에서는 머리(단계·제목·안내)와 몸(입력·버튼·링크)이 두 칸으로 나란히 선다.
 */
export function AuthCard({
  step,
  title,
  description,
  notice,
  focusFirstField = false,
  children,
}: AuthCardProps) {
  const titleId = useId();
  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!focusFirstField || !window.matchMedia(POINTER_DEVICE_QUERY).matches)
      return;
    cardRef.current
      ?.querySelector<HTMLElement>("input, select")
      ?.focus({ preventScroll: true });
  }, [focusFirstField]);

  return (
    <Box
      component="section"
      ref={cardRef}
      aria-labelledby={titleId}
      sx={AUTH_CARD_STYLES.card}
    >
      <Box data-auth-part="head">
        {step ? (
          <Box sx={AUTH_CARD_STYLES.step}>
            {step.current} / {step.total}
          </Box>
        ) : null}
        <Box component="h1" id={titleId} sx={AUTH_CARD_STYLES.title}>
          {title}
        </Box>
        {description ? (
          <Box component="p" sx={AUTH_CARD_STYLES.description}>
            {description}
          </Box>
        ) : null}
        {notice ? <Box sx={AUTH_CARD_STYLES.notice}>{notice}</Box> : null}
      </Box>
      <Box data-auth-part="body">{children}</Box>
    </Box>
  );
}

/** 입력 칸 묶음 — `tight` 는 칸이 많은 단계(회원 정보)에서 간격을 줄인다 */
export function AuthFields({
  tight = false,
  children,
}: {
  tight?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      data-auth-part="fields"
      sx={[AUTH_CARD_STYLES.fields, tight && AUTH_CARD_STYLES.fieldsTight]}
    >
      {children}
    </Box>
  );
}

/** 두 칸을 한 줄에(직무·분야) */
export function AuthFieldRow({ children }: { children: ReactNode }) {
  return <Box sx={AUTH_CARD_STYLES.row}>{children}</Box>;
}
